import { generateAlgoliaFiltersFromBrowseFilters } from '@/search/browse_filters'
import { ASSESSMENTS_INDEX, ASSESSMENTS_SORTED_INDEX } from '@/search/algolia_search'
import { KEYWORD_KEY, withCleared } from '@/browse/browse_state'
import type { BrowseState } from '@/browse/browse_state'
import type { BrowseSearchConfig, CountOnlyFacet, CountQueryPlan, SearchFilterDefinition, SearchPlan } from '@/browse/browse_search'
import { GRADES } from '@/browse/grades'
import { assessmentCategoriesFor, assessmentSubjectsForProfile } from '@/browse/assessments/assessment_taxonomy'

export const MAX_ASSESSMENT_RESULTS = 300
export const NON_PREMIUM_RESULT_LIMIT = 2
export const DEFAULT_LANGUAGE = 'English'
export const SPANISH = 'Spanish'
export const LANGUAGES = [DEFAULT_LANGUAGE, SPANISH]
export const BASELINE = 'Baseline'
export const ASSESSMENT_TYPES = [BASELINE, 'Progress Monitoring']

export const ALGOLIA_ATTRIBUTE_BY_FILTER_KEY: Record<string, string> = {
  subject: 'subject',
  grades: 'grades',
  category: 'domains',
  type: 'type',
  language: 'language'
}

// These controls show only once a subject is chosen, and a filter whose control is hidden
// does not apply.
const FILTER_KEYS_SHOWN_WITH_A_SUBJECT = ['grades', 'category', 'type']
const PREMIUM_FILTER_KEYS = [KEYWORD_KEY, 'category', 'grades']

export interface AssessmentBrowseContext {
  isPremiumUser: () => boolean
  isReadyForSearch: () => boolean
}

export function selectedSubjectOf({ refinements }: BrowseState): string {
  return refinements.subject[0] ?? ''
}

export function selectedLanguageOf({ refinements }: BrowseState): string {
  return refinements.language[0] ?? DEFAULT_LANGUAGE
}

export function isBaselineSelected({ refinements }: BrowseState): boolean {
  return refinements.type[0] === BASELINE
}

function isSubjectSelected(state: BrowseState): boolean {
  return Boolean(selectedSubjectOf(state))
}

function isNonEnglishSelected(state: BrowseState): boolean {
  return selectedLanguageOf(state) !== DEFAULT_LANGUAGE
}

function withDefaultLanguage(state: BrowseState): BrowseState {
  if (state.refinements.language?.length) {
    return state
  }
  return { ...state, refinements: { ...state.refinements, language: [DEFAULT_LANGUAGE] } }
}

function filterKeysWithHiddenControls(state: BrowseState): string[] {
  if (!isSubjectSelected(state)) {
    return FILTER_KEYS_SHOWN_WITH_A_SUBJECT
  }
  const hasCategories = assessmentCategoriesFor(selectedSubjectOf(state)).length > 0
  if (!hasCategories) {
    return ['category']
  }
  return []
}

function withoutPremiumFilters(state: BrowseState, context: AssessmentBrowseContext): BrowseState {
  if (context.isPremiumUser()) {
    return state
  }
  return withCleared(state, PREMIUM_FILTER_KEYS)
}

// The URL can hold filters the sidebar would not apply (a shared or hand-edited link), so they are
// dropped here and a reload shows what a click did.
function normalizeAssessmentBrowseState(state: BrowseState, context: AssessmentBrowseContext): BrowseState {
  const stateWithLanguage = withDefaultLanguage(state)
  const stateWithVisibleFilters = withCleared(stateWithLanguage, filterKeysWithHiddenControls(stateWithLanguage))
  return withoutPremiumFilters(stateWithVisibleFilters, context)
}

const ASSESSMENT_FACETS: SearchFilterDefinition[] = [
  {
    key: 'subject',
    attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.subject,
    clearsOnChange: ['grades', 'category'],
    alsoClearsWhenEmptied: ['language'],
    validValues: assessmentSubjectsForProfile
  },
  { key: 'grades', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.grades, multiple: true, validValues: () => GRADES },
  {
    key: 'category',
    attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.category,
    validValues: (state) => assessmentCategoriesFor(selectedSubjectOf(state))
  },
  { key: 'type', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.type, validValues: () => ASSESSMENT_TYPES },
  { key: 'language', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.language, validValues: () => LANGUAGES }
]

// The grade row lists every grade the subject has, whatever else is chosen.
const SUBJECT_GRADES: CountOnlyFacet = { key: 'subjectGrades', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.grades }

// Counted under every other filter, so each option says what choosing it would return.
const FACETS_COUNTED_UNDER_EVERY_OTHER_FILTER = ['grades', 'category', 'type']

function selectedKeys({ keyword, refinements }: BrowseState): string[] {
  const refinedKeys = Object.keys(refinements).filter((key) => refinements[key].length > 0)
  if (!keyword) {
    return refinedKeys
  }
  return [...refinedKeys, KEYWORD_KEY]
}

function selectedKeysExcept(state: BrowseState, keptKeys: string[]): string[] {
  return selectedKeys(state).filter((key) => !keptKeys.includes(key))
}

// A count that leaves out no chosen filter matches the results query, so it rides on that query.
function withCount(plan: SearchPlan, countQuery: CountQueryPlan): SearchPlan {
  const matchesResultsQuery = countQuery.excludedKeys.length === 0 && countQuery.appliesAlgoliaFilters
  if (matchesResultsQuery) {
    return { ...plan, hitsFacetKeys: [...plan.hitsFacetKeys, ...countQuery.facetKeys] }
  }
  return { ...plan, countQueries: [...plan.countQueries, countQuery] }
}

function countUnderEveryOtherFilter(state: BrowseState, key: string): CountQueryPlan {
  const isKeySelected = selectedKeys(state).includes(key)
  const excludedKeys = isKeySelected ? [key] : []
  return { facetKeys: [key], excludedKeys, appliesAlgoliaFilters: true }
}

// A subject is disabled only for having nothing in the chosen non-English language.
function subjectsInLanguageCount(state: BrowseState): CountQueryPlan {
  return { facetKeys: ['subject'], excludedKeys: selectedKeysExcept(state, ['language']), appliesAlgoliaFilters: false }
}

function subjectGradesCount(state: BrowseState): CountQueryPlan {
  return { facetKeys: [SUBJECT_GRADES.key], excludedKeys: selectedKeysExcept(state, ['subject']), appliesAlgoliaFilters: true }
}

function countsForChosenSubjectAndLanguage(state: BrowseState): CountQueryPlan[] {
  const counts: CountQueryPlan[] = []
  if (isNonEnglishSelected(state)) {
    counts.push(subjectsInLanguageCount(state))
  }
  if (isSubjectSelected(state)) {
    counts.push(subjectGradesCount(state))
  }
  return counts
}

const EMPTY_PLAN: SearchPlan = { hitsFacetKeys: [], countQueries: [] }

export function assessmentSearchPlan(state: BrowseState): SearchPlan {
  const facetCounts = FACETS_COUNTED_UNDER_EVERY_OTHER_FILTER.map((key) => countUnderEveryOtherFilter(state, key))
  const counts = [...facetCounts, ...countsForChosenSubjectAndLanguage(state)]
  return counts.reduce(withCount, EMPTY_PLAN)
}

function blvDhhExclusionFilter({ keyword }: BrowseState): string {
  return generateAlgoliaFiltersFromBrowseFilters({ keyword, characteristics: [] })
}

// Non-premium users see a two-result preview, so Algolia is asked for no more than that.
function resultLimitFor(context: AssessmentBrowseContext): number {
  if (context.isPremiumUser()) {
    return MAX_ASSESSMENT_RESULTS
  }
  return NON_PREMIUM_RESULT_LIMIT
}

// A keyword search is ranked by relevance; browsing without one follows the replica's sort key.
export function assessmentsIndexFor({ keyword }: BrowseState): string {
  if (keyword) {
    return ASSESSMENTS_INDEX
  }
  return ASSESSMENTS_SORTED_INDEX
}

// Vue 2 Browse Assessments names the filter type before the value (decision 7b).
const CHIP_FILTER_TYPE_NAMES: Record<string, string> = {
  subject: 'subject',
  category: 'category',
  grades: 'grade',
  type: 'assessment type',
  language: 'language',
  [KEYWORD_KEY]: 'keyword'
}

export function assessmentChipRemoveButtonName(key: string, label: string): string {
  const filterTypeName = CHIP_FILTER_TYPE_NAMES[key]
  return filterTypeName ? `Remove ${filterTypeName} ${label} filter` : `Remove ${label} filter`
}

export function assessmentBrowseConfig(context: AssessmentBrowseContext): BrowseSearchConfig {
  return {
    facets: ASSESSMENT_FACETS,
    countOnlyFacets: [SUBJECT_GRADES],
    chipRows: [
      { label: 'Subject', keys: ['subject', 'category'] },
      { label: 'Grades', keys: ['grades'] },
      { label: 'Assessment', keys: ['type'] },
      { label: 'Language', keys: ['language'], omitValues: [DEFAULT_LANGUAGE] },
      { label: 'Keywords', keys: [KEYWORD_KEY] }
    ],
    hasKeywordSearch: true,
    // Kept, ref would re-report the entry point on every reload or shared link of a filtered page.
    paramsDroppedOnChange: ['ref'],
    hitsPerPage: () => resultLimitFor(context),
    indexName: assessmentsIndexFor,
    algoliaFilters: blvDhhExclusionFilter,
    normalize: (state) => normalizeAssessmentBrowseState(state, context),
    searchPlan: assessmentSearchPlan,
    isReadyForSearch: context.isReadyForSearch
  }
}
